import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Tag, Modal, Form, Input, Select, DatePicker, InputNumber, Switch,
  Typography, Row, Col, Statistic, Popconfirm, Tooltip, message, Alert,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  CalendarOutlined, TeamOutlined, StarOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { eventsApi, Event, EventCreatePayload, EventStats } from '../../api/events';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const CATEGORY_CONFIG: Record<string, { label: string; color: string }> = {
  concert: { label: 'Концерт', color: 'purple' },
  sport: { label: 'Спорт', color: 'blue' },
  holiday: { label: 'Праздник', color: 'red' },
  exhibition: { label: 'Выставка', color: 'cyan' },
  workshop: { label: 'Мастер-класс', color: 'orange' },
  kids: { label: 'Детское', color: 'green' },
  other: { label: 'Другое', color: 'default' },
};

const EventsList: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<EventStats | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Event | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const [messageApi, contextHolder] = message.useMessage();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [eventsRes, statsRes] = await Promise.allSettled([
        eventsApi.list(),
        eventsApi.stats(),
      ]);
      if (eventsRes.status === 'fulfilled') setEvents(eventsRes.value.data);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ category: 'other', is_free: true });
    setModalOpen(true);
  };

  const openEdit = (record: Event) => {
    setEditing(record);
    form.setFieldsValue({
      ...record,
      dates: [
        record.start_date ? dayjs(record.start_date) : null,
        record.end_date ? dayjs(record.end_date) : null,
      ].filter(Boolean),
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    let values: any;
    try { values = await form.validateFields(); } catch { return; }

    setSubmitting(true);
    try {
      const payload: EventCreatePayload = {
        title_ru: values.title_ru,
        title_kk: values.title_kk || values.title_ru,
        description_ru: values.description_ru,
        description_kk: values.description_kk,
        image_url: values.image_url,
        location_name: values.location_name,
        address: values.address,
        start_date: values.dates?.[0]?.toISOString(),
        end_date: values.dates?.[1]?.toISOString(),
        category: values.category,
        organizer: values.organizer,
        price: values.is_free ? undefined : values.price,
        is_free: values.is_free,
        max_participants: values.max_participants,
      };

      if (editing) {
        await eventsApi.update(editing.id, payload);
        messageApi.success('Событие обновлено');
      } else {
        await eventsApi.create(payload);
        messageApi.success('Событие создано');
      }
      setModalOpen(false);
      fetchData();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await eventsApi.delete(id);
      messageApi.success('Событие удалено');
      fetchData();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const columns: ColumnsType<Event> = [
    {
      title: 'Название',
      dataIndex: 'title_ru',
      key: 'title_ru',
      ellipsis: true,
      render: (title: string, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{title}</Text>
          {record.organizer && <Text type="secondary" style={{ fontSize: 12 }}>{record.organizer}</Text>}
        </Space>
      ),
    },
    {
      title: 'Категория',
      dataIndex: 'category',
      key: 'category',
      width: 130,
      render: (cat: string) => {
        const cfg = CATEGORY_CONFIG[cat] || { label: cat, color: 'default' };
        return <Tag>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Дата',
      key: 'dates',
      width: 140,
      render: (_: any, r: Event) => (
        <Space direction="vertical" size={0}>
          <Text>{dayjs(r.start_date).format('DD.MM.YYYY')}</Text>
          {r.end_date && <Text type="secondary" style={{ fontSize: 12 }}>до {dayjs(r.end_date).format('DD.MM.YYYY')}</Text>}
        </Space>
      ),
    },
    {
      title: 'Место',
      dataIndex: 'location_name',
      key: 'location_name',
      width: 160,
      ellipsis: true,
      render: (v: string) => v || <Text type="secondary">--</Text>,
    },
    {
      title: 'Участники',
      key: 'participants',
      width: 120,
      align: 'center',
      render: (_: any, r: Event) => (
        <Text>{r.registered_count}{r.max_participants ? ` / ${r.max_participants}` : ''}</Text>
      ),
    },
    {
      title: 'Цена',
      key: 'price',
      width: 100,
      render: (_: any, r: Event) =>
        r.is_free ? <Tag>Бесплатно</Tag> : <Text>{r.price?.toLocaleString('ru-RU')} тг</Text>,
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 100,
      render: (_: any, record: Event) => (
        <Space size={4}>
          <Tooltip title="Редактировать">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Popconfirm title="Удалить событие?" onConfirm={() => handleDelete(record.id)} okText="Да" cancelText="Нет">
            <Tooltip title="Удалить">
              <Button size="small" icon={<DeleteOutlined />} danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {contextHolder}

      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={3} style={{ margin: 0, color: 'var(--l-ink)' }}>Афиша</Title>
          <Text type="secondary">Мероприятия и события города Аксай</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} style={{ background: 'var(--l-accent)' }}>
          Добавить событие
        </Button>
      </div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card><Statistic title="Всего событий" value={stats?.total_events ?? events.length} prefix={<CalendarOutlined />} /></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ }}><Statistic title="Предстоящих" value={stats?.upcoming ?? 0} valueStyle={{ color: 'var(--l-accent)' }} prefix={<StarOutlined />} /></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ }}><Statistic title="Всего регистраций" value={stats?.total_registrations ?? 0} valueStyle={{ color: 'var(--l-ok)' }} prefix={<TeamOutlined />} /></Card>
        </Col>
      </Row>

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      <Card bodyStyle={{ padding: 0 }} extra={<Button icon={<ReloadOutlined />} onClick={fetchData}>Обновить</Button>}>
        <Table<Event> rowKey="id" columns={columns} dataSource={events} loading={loading} pagination={{ pageSize: 20, showTotal: t => `Всего: ${t}` }} locale={{ emptyText: 'События не найдены' }} scroll={{ x: 900 }} />
      </Card>

      {/* Create / Edit Modal */}
      <Modal title={editing ? 'Редактировать событие' : 'Добавить событие'} open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleSubmit} confirmLoading={submitting} okText={editing ? 'Сохранить' : 'Создать'} cancelText="Отмена" width={700} destroyOnClose>
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="title_ru" label="Название (рус.)" rules={[{ required: true, message: 'Введите название' }]}>
                <Input placeholder="Концерт ко Дню города" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="title_kk" label="Атауы (каз.)">
                <Input placeholder="Қала күніне арналған концерт" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="description_ru" label="Описание (рус.)">
                <TextArea rows={3} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="description_kk" label="Сипаттамасы (каз.)">
                <TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="category" label="Категория" rules={[{ required: true }]}>
                <Select>
                  {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                    <Select.Option key={key} value={key}>{cfg.label}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="dates" label="Даты проведения" rules={[{ required: true, message: 'Выберите даты' }]}>
                <RangePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="location_name" label="Место">
                <Input placeholder="ДК Юбилейный" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="address" label="Адрес">
                <Input placeholder="ул. Ленина, 15" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="organizer" label="Организатор">
                <Input placeholder="Акимат г. Аксай" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="max_participants" label="Макс. участников">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="image_url" label="Ссылка на изображение">
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="is_free" label="Бесплатное" valuePropName="checked">
                <Switch checkedChildren="Да" unCheckedChildren="Нет" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.is_free !== cur.is_free}>
                {({ getFieldValue }) =>
                  !getFieldValue('is_free') ? (
                    <Form.Item name="price" label="Цена (тг)">
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default EventsList;
